// Package Imports
import { message } from "antd";
import { useCallback, useEffect, useState } from "react";
import { useMutation } from "@apollo/client";

// Internal Imports
import { parseError } from "../utils/parseError";
import { ADD_USER_TO_GROUP } from "../graphql/Mutations";
import mixpanelService from "../services/mixpanel";
import bugfenderHelper from "../services/bugfender";

interface IAddUserToGroupArgs {
  username: string;
  groupName: string;
  onSuccessCallback?: () => void;
  onErrorCallback?: (error: any) => void;
}

export const useAddUserToGroup = ({
  onSuccessCallback,
  onErrorCallback
}: {
  onSuccessCallback?: () => void;
  onErrorCallback?: (error: any) => void;
} = {}) => {

  const [loading, setLoading] = useState(false);

  const [
    addUserToGroupMutation,
    {
      loading: mutationLoading,
      data: mutationData,
      error: mutationError,
      reset: mutationReset,
    },
  ] = useMutation(ADD_USER_TO_GROUP);

  const handleAddUserToGroup = useCallback(
    async (args: IAddUserToGroupArgs) => {
      try {
        const response = await addUserToGroupMutation({
          variables: {
            input: {
              username: args.username,
              groupName: args.groupName
            }
          },
        });
        return response?.data;
      } catch (error) {
        console.error("useAddUserToGroup - Error encountered", error);
        bugfenderHelper.error(error);
        throw error;
      }
    },
    [addUserToGroupMutation]
  );

  // SIDE-EFFECT - Successful Add User To Group
  useEffect(() => {
    // CHECK - Mutation Data
    if (!mutationData) return;

    // VERIFY - Mutation Data
    if (mutationData?.adminAddUserToGroup?.Error) {
      setLoading(false);
      const errorMessage = mutationData.adminAddUserToGroup.Error.message;
      message.error(errorMessage);
      bugfenderHelper.error(errorMessage);
      
      if (onErrorCallback) {
        onErrorCallback(mutationData.adminAddUserToGroup.Error);
      }
      
      mutationReset();
      return;
    }

    // SUCCESS - User added to group
    if (mutationData?.adminAddUserToGroup?.text) {
      setLoading(false);
      message.success(mutationData.adminAddUserToGroup.text);
      
      // Track success in mixpanel
      mixpanelService.track("App.User.Added.To.Group", {
        success: true,
        message: mutationData.adminAddUserToGroup.text
      });

      if (onSuccessCallback) {
        onSuccessCallback();
      }
    }
  }, [mutationData, onSuccessCallback, onErrorCallback, mutationReset]);

  // SIDE-EFFECT - Failed Add User To Group
  useEffect(() => {
    // CHECK - Mutation Error
    if (!mutationError) return;

    const errorMessage = parseError(mutationError);
    setLoading(false);
    message.error(errorMessage);
    bugfenderHelper.error(mutationError);

    // Track error in mixpanel
    mixpanelService.track("App.User.Added.To.Group", {
      success: false,
      error: errorMessage
    });

    if (onErrorCallback) {
      onErrorCallback(mutationError);
    }
  }, [mutationError, onErrorCallback]);

  // SIDE-EFFECT - Loading State
  useEffect(() => {
    if (mutationLoading) {
      setLoading(true);
    }

    if (!mutationLoading) {
      setLoading(false);
    }
  }, [mutationLoading]);

  return {
    loading,
    mutationData,
    handleAddUserToGroup,
    reset: mutationReset
  };
};
